// ✅ CreateVisitPage.js (FIXED CAMERA + PHOTO + SUMMARY)
// Visitor Management – Create Visit Screen (API CONNECTED + PHOTO)

import React, { useState, useRef, useEffect } from "react";
import axiosClient from "../../../api/axiosClient";
import { useNavigate } from "react-router-dom";

function CreateVisitPage() {
  const navigate = useNavigate();

  // -----------------------------
  // Core form state
  // -----------------------------
  const [visitor, setVisitor] = useState({
    name: "",
    mobile: "",
    email: "",
    govIdType: "",
    govIdNumber: "",
    company: "",
  });

  const [host, setHost] = useState({
    name: "",
    mobile: "",
    email: "",
    department: "",
  });

  const [visit, setVisit] = useState({
    date: "",
    time: "",
    purpose: "",
    notes: "",
  });

  const [submitting, setSubmitting] = useState(false);

  // -----------------------------
  // Photo / Camera state
  // -----------------------------
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [isCameraOn, setIsCameraOn] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState(null); // full data URL
  const [hasPhoto, setHasPhoto] = useState(false);

  // -----------------------------
  // Handlers for input fields
  // -----------------------------
  const handleVisitorChange = (e) => {
    const { name, value } = e.target;
    setVisitor((prev) => ({ ...prev, [name]: value }));
  };

  const handleHostChange = (e) => {
    const { name, value } = e.target;
    setHost((prev) => ({ ...prev, [name]: value }));
  };

  const handleVisitChange = (e) => {
    const { name, value } = e.target;
    setVisit((prev) => ({ ...prev, [name]: value }));
  };

  // -----------------------------
  // Camera control helpers
  // -----------------------------
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
  };

  const startCamera = async () => {
    try {
      console.log("🎥 Requesting camera access...");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      console.log("✅ Camera stream obtained", stream);
      streamRef.current = stream;
      // DO NOT set videoRef.current.srcObject here – video may not exist yet.
      setIsCameraOn(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      window.alert("Unable to access camera. Please check permissions.");
    }
  };

  const captureFromCamera = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const width = 320;
    const height = 240;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg");
    setPhotoDataUrl(dataUrl);
    setHasPhoto(true);

    // Stop camera after capture
    stopCamera();
  };

  // Single main button logic: "Capture Photo" -> "Take Photo"
  const handlePhotoButtonClick = async () => {
    console.log(
      "Photo button click. isCameraOn=",
      isCameraOn,
      "hasPhoto=",
      hasPhoto
    );
    if (!isCameraOn && !hasPhoto) {
      // Camera is off and no photo yet -> start camera
      await startCamera();
    } else if (isCameraOn) {
      // Camera is on -> capture photo
      captureFromCamera();
    }
  };

  const handleRetakePhoto = async () => {
    setPhotoDataUrl(null);
    setHasPhoto(false);
    await startCamera();
  };

  const handleRemovePhoto = () => {
    setPhotoDataUrl(null);
    setHasPhoto(false);
    stopCamera();
  };

  // 🔧 IMPORTANT FIX:
  // When camera is ON and we have a stream, attach it to the video element.
  useEffect(() => {
    if (isCameraOn && videoRef.current && streamRef.current) {
      console.log("🔁 Attaching stream to video element");
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isCameraOn]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // Submit handler (API connected)
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Strip base64 header if present
      let photoBase64 = null;
      if (photoDataUrl) {
        const parts = photoDataUrl.split(",");
        photoBase64 = parts.length > 1 ? parts[1] : photoDataUrl;
      }

      const payload = {
        visitor: {
          name: visitor.name,
          mobile: visitor.mobile,
          email: visitor.email,
          gov_id_type: visitor.govIdType,
          gov_id_number: visitor.govIdNumber,
          company: visitor.company,
          photo_base64: photoBase64, // optional, backend can use later
        },
        host: {
          name: host.name,
          mobile: host.mobile,
          email: host.email,
          department: host.department,
        },
        visit: {
          visit_date: visit.date,
          visit_time: visit.time,
          purpose: visit.purpose,
          notes: visit.notes,
        },
      };

      console.log("Submitting payload:", payload);

      const res = await axiosClient.post("/visitor-management/visits", payload);
      const data = res.data?.data;

      window.alert("Visit created successfully! Visit ID: " + data.visit_id);
      navigate(`/visitor/approvals/${data.visit_id}`);
    } catch (err) {
      console.error("Error creating visit:", err);

      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to create visit.";

      window.alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Label for the main photo button
  const mainPhotoButtonLabel = isCameraOn
    ? "Take Photo"
    : hasPhoto
    ? "Retake Photo"
    : "Capture Photo";

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-0">Create Visit</h2>
          <small className="text-muted">
            Register a new visitor and send approval request to the host.
          </small>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          {/* LEFT COLUMN – Visitor & Host & Visit */}
          <div className="col-lg-8">
            {/* Visitor Details */}
            <div className="card shadow-sm mb-3" style={{ borderRadius: "18px" }}>
              <div className="card-header bg-white border-0">
                <h5 className="mb-0 fw-semibold">Visitor Details</h5>
                <small className="text-muted">
                  Basic identity details. At least Mobile / Email OR Govt ID is
                  required.
                </small>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Visitor Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={visitor.name}
                      onChange={handleVisitorChange}
                      placeholder="Rajesh Kumar"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Mobile</label>
                    <input
                      type="text"
                      className="form-control"
                      name="mobile"
                      value={visitor.mobile}
                      onChange={handleVisitorChange}
                      placeholder="10-digit mobile"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={visitor.email}
                      onChange={handleVisitorChange}
                      placeholder="name@email.com"
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Govt ID Type</label>
                    <select
                      className="form-select"
                      name="govIdType"
                      value={visitor.govIdType}
                      onChange={handleVisitorChange}
                    >
                      <option value="">Select</option>
                      <option value="AADHAAR">Aadhaar</option>
                      <option value="PAN">PAN</option>
                      <option value="DL">Driving License</option>
                      <option value="PASSPORT">Passport</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  <div className="col-md-5">
                    <label className="form-label">Govt ID Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="govIdNumber"
                      value={visitor.govIdNumber}
                      onChange={handleVisitorChange}
                      placeholder="ID number"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Company / Organisation</label>
                    <input
                      type="text"
                      className="form-control"
                      name="company"
                      value={visitor.company}
                      onChange={handleVisitorChange}
                      placeholder="XYZ Company"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Host Details */}
            <div className="card shadow-sm mb-3" style={{ borderRadius: "18px" }}>
              <div className="card-header bg-white border-0">
                <h5 className="mb-0 fw-semibold">Host Details</h5>
                <small className="text-muted">
                  Manual entry for now. Later can be linked to employee master.
                </small>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Host Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={host.name}
                      onChange={handleHostChange}
                      placeholder="Manish"
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Host Mobile</label>
                    <input
                      type="text"
                      className="form-control"
                      name="mobile"
                      value={host.mobile}
                      onChange={handleHostChange}
                      placeholder="Mobile"
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Host Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={host.email}
                      onChange={handleHostChange}
                      placeholder="email@company.com"
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">Department</label>
                    <input
                      type="text"
                      className="form-control"
                      name="department"
                      value={host.department}
                      onChange={handleHostChange}
                      placeholder="Dept"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Visit Details */}
            <div className="card shadow-sm mb-4" style={{ borderRadius: "18px" }}>
              <div className="card-header bg-white border-0">
                <h5 className="mb-0 fw-semibold">Visit Details</h5>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Visit Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={visit.date}
                      onChange={handleVisitChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Visit Time</label>
                    <input
                      type="time"
                      className="form-control"
                      name="time"
                      value={visit.time}
                      onChange={handleVisitChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Purpose of Visit</label>
                    <input
                      type="text"
                      className="form-control"
                      name="purpose"
                      value={visit.purpose}
                      onChange={handleVisitChange}
                      placeholder="Project discussion / Interview / Service etc."
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Additional Notes</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      name="notes"
                      value={visit.notes}
                      onChange={handleVisitChange}
                      placeholder="Any special instructions..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN – Photo + Summary */}
          <div className="col-lg-4">
            <div className="card shadow-sm mb-3" style={{ borderRadius: "18px" }}>
              <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-semibold">Visitor Photo & Summary</h5>
              </div>

              <div className="card-body">
                {/* PHOTO AREA */}
                <div className="mb-3 text-center">
                  {/* Hidden canvas used for capture */}
                  <canvas ref={canvasRef} style={{ display: "none" }} />

                  {/* Live video or captured photo */}
                  {isCameraOn && (
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      style={{
                        width: "320px",
                        height: "240px",
                        borderRadius: "12px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                      }}
                    />
                  )}

                  {!isCameraOn && hasPhoto && photoDataUrl && (
                    <img
                      src={photoDataUrl}
                      alt="Captured visitor"
                      style={{
                        width: "320px",
                        height: "240px",
                        borderRadius: "12px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                      }}
                    />
                  )}

                  {!isCameraOn && !hasPhoto && (
                    <div
                      style={{
                        width: "320px",
                        height: "240px",
                        borderRadius: "12px",
                        border: "1px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.9rem",
                        color: "#888",
                        margin: "0 auto",
                      }}
                    >
                      No photo captured
                    </div>
                  )}
                </div>

                {/* PHOTO BUTTONS */}
                <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
                  {!hasPhoto && (
                    <button
                      type="button"
                      onClick={handlePhotoButtonClick}
                      className="btn btn-outline-primary btn-sm fw-semibold"
                    >
                      {mainPhotoButtonLabel}
                    </button>
                  )}

                  {hasPhoto && (
                    <>
                      <button
                        type="button"
                        onClick={handleRetakePhoto}
                        className="btn btn-outline-primary btn-sm fw-semibold"
                      >
                        Retake Photo
                      </button>
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="btn btn-outline-secondary btn-sm"
                      >
                        Remove Photo
                      </button>
                    </>
                  )}
                </div>

                <hr />

                {/* SUMMARY (with small thumbnail if available) */}
                <div className="small">
                  {hasPhoto && photoDataUrl && (
                    <div className="d-flex align-items-center mb-2">
                      <img
                        src={photoDataUrl}
                        alt="Thumbnail"
                        style={{
                          width: "64px",
                          height: "48px",
                          borderRadius: "8px",
                          objectFit: "cover",
                          border: "1px solid #ddd",
                          marginRight: "8px",
                        }}
                      />
                      <span className="text-muted">Captured visitor photo</span>
                    </div>
                  )}

                  <p className="mb-1">
                    <strong>Visitor:</strong>{" "}
                    {visitor.name || <span className="text-muted">Not set</span>}
                  </p>

                  <p className="mb-1">
                    <strong>Contact:</strong>{" "}
                    {visitor.mobile ||
                      visitor.email || (
                        <span className="text-muted">Not set</span>
                      )}
                  </p>

                  <p className="mb-1">
                    <strong>Company:</strong>{" "}
                    {visitor.company || (
                      <span className="text-muted">Not set</span>
                    )}
                  </p>

                  <hr />

                  <p className="mb-1">
                    <strong>Host:</strong>{" "}
                    {host.name || <span className="text-muted">Not set</span>}
                  </p>
                  <p className="mb-1">
                    <strong>Host Contact:</strong>{" "}
                    {host.mobile ||
                      host.email || (
                        <span className="text-muted">Not set</span>
                      )}
                  </p>
                  <p className="mb-1">
                    <strong>Department:</strong>{" "}
                    {host.department || (
                      <span className="text-muted">Not set</span>
                    )}
                  </p>

                  <hr />

                  <p className="mb-1">
                    <strong>Visit Date & Time:</strong>{" "}
                    {visit.date || visit.time ? (
                      <>
                        {visit.date || "Date?"} • {visit.time || "Time?"}
                      </>
                    ) : (
                      <span className="text-muted">Not set</span>
                    )}
                  </p>

                  <p className="mb-1">
                    <strong>Purpose:</strong>{" "}
                    {visit.purpose || (
                      <span className="text-muted">Not set</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* ACTION CARD */}
            <div className="card shadow-sm" style={{ borderRadius: "18px" }}>
              <div className="card-body">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary w-100 mb-2 fw-semibold"
                >
                  {submitting
                    ? "Submitting..."
                    : "Register Visit & Send for Approval"}
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 btn-sm"
                  onClick={() => {
                    setVisitor({
                      name: "",
                      mobile: "",
                      email: "",
                      govIdType: "",
                      govIdNumber: "",
                      company: "",
                    });
                    setHost({
                      name: "",
                      mobile: "",
                      email: "",
                      department: "",
                    });
                    setVisit({
                      date: "",
                      time: "",
                      purpose: "",
                      notes: "",
                    });
                    handleRemovePhoto();
                  }}
                >
                  Clear Form
                </button>

                <p className="small text-muted mt-2 mb-0">
                  🔒 Validation & QR are handled by backend. Photo is optional and can
                  be linked to visitor profile later.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreateVisitPage;
